import { useState, useMemo } from "react";
import {
  useV1ProductionBomRevisionsList,
  useV1ProductionBomItemsList,
  useV1ProductionRoutingRevisionsList,
  useV1ProductionRoutingOperationsList,
  v1ProductionBomItemsList,
  useV1ProductionOperationMaterialSourcesList,
  useV1ProductionOperationMaterialSourcesCreate,
  useV1ProductionOperationMaterialSourcesDestroy,
} from "@/api/generated/v1/v1";

type BomItem = NonNullable<Awaited<ReturnType<typeof v1ProductionBomItemsList>>["results"]>[0];
import {
  useBomOperationAllocationsQuery,
  useCreateBomOperationAllocationMutation,
  useActivateBomOperationAllocationMutation,
  useDeactivateBomOperationAllocationMutation,
} from "../api/bomAllocationApi";
import { PageBanner } from "@/shared/components/PageBanner";
import { usePermission } from "@/auth/usePermission";
import { DependencyGraphEditor } from "@/features/routing/DependencyGraphEditor";
import { useEffect, useRef, useCallback } from "react";

export function BomOperationAllocationPage() {
  const canManage = usePermission("production.manage");

  // Selection State
  const [selectedBomRevisionId, setSelectedBomRevisionId] = useState<string>("");
  const [selectedRoutingRevisionId, setSelectedRoutingRevisionId] = useState<string>("");

  // Form State
  const [selectedBomItemId, setSelectedBomItemId] = useState<string>("");
  const [selectedRoutingOpId, setSelectedRoutingOpId] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Queries
  // 1. BOM Revisions (Server-side status=published filter)
  const { data: bomRevisionsData, isLoading: bomRevLoading } = useV1ProductionBomRevisionsList(
    { status: "published", page_size: 100 } as any
  );
  const publishedBomRevisions = bomRevisionsData?.results || [];

  // 2. Routing Revisions (Client-side status=published filter for AM-011.1 compliance)
  const { data: routingRevisionsData, isLoading: routingRevLoading } = useV1ProductionRoutingRevisionsList(
    { page_size: 100 } as any
  );
  const publishedRoutingRevisions = useMemo(() => {
    return (routingRevisionsData?.results || []).filter((r: any) => r.status === "published");
  }, [routingRevisionsData]);

  // 3. Items of selected Published BOM Revision
  const { data: bomItemsData, isLoading: bomItemsLoading } = useV1ProductionBomItemsList(
    selectedBomRevisionId ? ({ bom_revision: selectedBomRevisionId, page_size: 1000 } as any) : undefined,
    { query: { enabled: !!selectedBomRevisionId } }
  );
  const bomItems = bomItemsData?.results || [];

  // 4. Operations of selected Published Routing Revision
  const { data: routingOpsData, isLoading: routingOpsLoading } = useV1ProductionRoutingOperationsList(
    selectedRoutingRevisionId ? ({ routing_revision: selectedRoutingRevisionId, page_size: 1000 } as any) : undefined,
    { query: { enabled: !!selectedRoutingRevisionId } }
  );
  const routingOps = routingOpsData?.results || [];

  // 5. Allocations list
  const { data: allocations = [], isLoading: allocationsLoading } = useBomOperationAllocationsQuery();

  // 6. Material Sources list (AM-030)
  const { data: materialSourcesData } = useV1ProductionOperationMaterialSourcesList({ page_size: 1000 } as any);
  const materialSources = materialSourcesData?.results || [];

  // Mutations
  const createMutation = useCreateBomOperationAllocationMutation();
  const activateMutation = useActivateBomOperationAllocationMutation();
  const deactivateMutation = useDeactivateBomOperationAllocationMutation();
  const linkSourceMutation = useV1ProductionOperationMaterialSourcesCreate();
  const unlinkSourceMutation = useV1ProductionOperationMaterialSourcesDestroy();

  // Client-side Filtered Allocations based on Selected BOM Revision and Routing Revision items/ops
  const filteredAllocations = useMemo(() => {
    if (!selectedBomRevisionId && !selectedRoutingRevisionId) {
      return allocations;
    }

    const bomItemIds = new Set(bomItems.map((item: any) => item.id));
    const routingOpIds = new Set(routingOps.map((op: any) => op.id));

    return allocations.filter((alloc) => {
      // Active filter
      if (!alloc.is_active) return false;
      const matchBom = selectedBomRevisionId ? bomItemIds.has(alloc.bom_item) : true;
      const matchRouting = selectedRoutingRevisionId ? routingOpIds.has(alloc.routing_operation) : true;
      return matchBom && matchRouting;
    });
  }, [allocations, bomItems, routingOps, selectedBomRevisionId, selectedRoutingRevisionId]);

  // STALE CLOSURE FIX: Keep latest state in a ref for the global Mermaid callback
  const latestStateRef = useRef({ selectedBomRevisionId, selectedRoutingRevisionId, selectedBomItemId });
  useEffect(() => {
    latestStateRef.current = { selectedBomRevisionId, selectedRoutingRevisionId, selectedBomItemId };
  }, [selectedBomRevisionId, selectedRoutingRevisionId, selectedBomItemId]);

  const onNodeClick = useCallback((nodeIdStr: string) => {
    const state = latestStateRef.current;
    if (!state.selectedBomRevisionId || !state.selectedRoutingRevisionId) return;
    
    // Extract actual ID (e.g. from "op_123" -> "123")
    const actualOpId = nodeIdStr.replace("op_", "");
    setSelectedRoutingOpId(actualOpId);
    
    // Note: If a BOM item is also selected, we could auto-trigger allocation here!
    // But for safety, we just select the node in the dropdown/form.
  }, []);

  useEffect(() => {
    // Bind to window for Mermaid
    (window as any).handleMermaidNodeClick = onNodeClick;
    return () => {
      delete (window as any).handleMermaidNodeClick;
    };
  }, [onNodeClick]);

  const handleCreateAllocation = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!selectedBomItemId || !selectedRoutingOpId) {
      setErrorMessage("لطفاً یک قطعه از BOM و یک ایستگاه کاری از مسیر را انتخاب کنید.");
      return;
    }

    try {
      await createMutation.mutateAsync({
        bom_item: selectedBomItemId,
        routing_operation: selectedRoutingOpId,
      });
      setSuccessMessage("تخصیص قطعه با موفقیت در سیستم ثبت گردید.");
      setSelectedBomItemId("");
      setSelectedRoutingOpId("");
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.non_field_errors?.[0] || err?.message;
      setErrorMessage(
        `خطا در ثبت تخصیص: ${detail || "احتمالاً این تخصیص تکراری است یا نسخه‌ها انتشار نیافته‌اند."}`
      );
    }
  };

  const handleToggleActive = async (allocId: string, currentStatus: boolean) => {
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      if (currentStatus) {
        await deactivateMutation.mutateAsync(allocId);
        setSuccessMessage("تخصیص مورد نظر غیرفعال گردید.");
      } else {
        await activateMutation.mutateAsync(allocId);
        setSuccessMessage("تخصیص مورد نظر با موفقیت فعال گردید.");
      }
    } catch (err: any) {
      setErrorMessage("خطا در تغییر وضعیت تخصیص: " + (err?.response?.data?.detail || err?.message));
    }
  };

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
      }}
    >
      {/* Page Banner */}
      <PageBanner
        title="تخصیص قطعات فرمول ساخت به ایستگاه‌های کاری"
        subtitle="مدیریت ارتباط قطعات BOM به گام‌های عملیاتی مسیر تولید جهت کنترل کمبود قطعه (Shortage Hold) و مصرف کارگاهی"
        icon="🔗"
        helpLink="/help/bom/allocations"
      />

      {/* KPI DASHBOARD CARDS */}
      <style>{`
        .kpi-card {
          padding: 1.25rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          background: #FFFFFF;
          border-radius: 16px;
          border: 1px solid var(--color-border, #E4DCC8);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
          position: relative;
          overflow: hidden;
        }
        .kpi-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.15);
          border-color: rgba(0, 114, 112, 0.3);
        }
      `}</style>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.5rem",
        }}
      >
        <div className="kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(0,114,112,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            🔗
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              تخصیص‌های ثبت‌شده
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#007270" }}>
                {allocationsLoading ? "..." : allocations.length}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#10B981" }}>فعال</span>
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(245,158,11,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            🧩
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              قطعات BOM (نسخه جاری)
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#D97706" }}>
                {bomItemsLoading ? "..." : bomItems.length}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#F59E0B" }}>ثبت شده</span>
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(59,130,246,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            🚉
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              ایستگاه‌های کاری
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#2563EB" }}>
                {routingOpsLoading ? "..." : routingOps.length}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#3B82F6" }}>آماده تخصیص</span>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Messages */}
      {errorMessage && (
        <div
          role="alert"
          style={{
            padding: "0.85rem 1.1rem",
            borderRadius: "10px",
            background: "rgba(239, 68, 68, 0.12)",
            border: "1px solid #EF4444",
            color: "#B91C1C",
            fontSize: "0.85rem",
            fontWeight: 600,
          }}
        >
          🚨 {errorMessage}
        </div>
      )}

      {successMessage && (
        <div
          role="status"
          style={{
            padding: "0.85rem 1.1rem",
            borderRadius: "10px",
            background: "rgba(16, 185, 129, 0.12)",
            border: "1px solid #10B981",
            color: "#047857",
            fontSize: "0.85rem",
            fontWeight: 600,
          }}
        >
          ✅ {successMessage}
        </div>
      )}

      {/* Filter Header: Selection of Published Revisions */}
      <div className="brand-card-extand" style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>

        <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#007270" }}>
          🎯 ۱. انتخاب نسخه‌های منتشرشده (Published Revisions Only)
        </h3>
        <p style={{ margin: 0, fontSize: "0.8rem", color: "#666" }}>
          طبق ضوابط حاکمیتی سیستم، تخصیص قطعات تنها برای نسخه‌های منتشرشده BOM و Routing مجاز است.
        </p>

        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          {/* Select BOM Revision */}
          <div style={{ flex: "1 1 300px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "6px" }}>
              نسخه BOM منتشرشده:
            </label>
            <select
              aria-label="انتخاب نسخه BOM"
              value={selectedBomRevisionId}
              onChange={(e) => {
                setSelectedBomRevisionId(e.target.value);
                setSelectedBomItemId("");
              }}
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: "8px",
                border: "1px solid var(--color-border, #E4DCC8)",
                fontSize: "0.85rem",
                outline: "none",
                background: "var(--color-surface, #F5F1E6)",
              }}
            >
              <option value="">همه نسخه‌های BOM یا یک نسخه را انتخاب کنید...</option>
              {bomRevLoading ? (
                <option disabled>در حال بارگذاری نسخه‌ها...</option>
              ) : (
                publishedBomRevisions.map((rev: any) => (
                  <option key={rev.id} value={rev.id}>
                    {rev.bom_family_display?.name || rev.bom_family_display?.code || "BOM"} - نسخه {rev.revision_no} (Published)
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Select Routing Revision */}
          <div style={{ flex: "1 1 300px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "6px" }}>
              نسخه مسیریابی (Routing) منتشرشده:
            </label>
            <select
              aria-label="انتخاب نسخه Routing"
              value={selectedRoutingRevisionId}
              onChange={(e) => {
                setSelectedRoutingRevisionId(e.target.value);
                setSelectedRoutingOpId("");
              }}
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: "8px",
                border: "1px solid var(--color-border, #E4DCC8)",
                fontSize: "0.85rem",
                outline: "none",
                background: "var(--color-surface, #F5F1E6)",
              }}
            >
              <option value="">همه نسخه‌های مسیر یا یک نسخه را انتخاب کنید...</option>
              {routingRevLoading ? (
                <option disabled>در حال بارگذاری نسخه‌ها...</option>
              ) : (
                publishedRoutingRevisions.map((rev: any) => {
                  const famName = rev.routing_family_display?.name || rev.routing_family_display?.code || rev.family?.name || rev.family?.code || `مسیر (${rev.id.slice(0, 8)})`;
                  return (
                    <option key={rev.id} value={rev.id}>
                      {famName} - نسخه {rev.revision_no} (Published)
                    </option>
                  );
                })
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Form: Add New Allocation (Only when management permissions & active selections) */}
      {canManage && selectedBomRevisionId && selectedRoutingRevisionId && (
        <form
          onSubmit={handleCreateAllocation}
          className="brand-card-extand"
          style={{
            padding: "1.25rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#007270" }}>
            ➕ ۲. افزودن تخصیص قطعه جدید به ایستگاه
          </h3>

          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "flex-end" }}>
            {/* Select BOM Item */}
            <div style={{ flex: "1 1 280px" }}>
              <label htmlFor="bom-item-select" style={{ fontSize: "0.8rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>
                انتخاب قطعه از BOM:
              </label>
              <select
                id="bom-item-select"
                aria-label="انتخاب قطعه از BOM"
                value={selectedBomItemId}
                onChange={(e) => setSelectedBomItemId(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--color-border, #E4DCC8)",
                  fontSize: "0.85rem",
                  outline: "none",
                }}
              >
                <option value="" disabled>
                  یک قطعه از BOM انتخاب کنید...
                </option>
                {bomItemsLoading ? (
                  <option disabled>در حال دریافت قطعات...</option>
                ) : bomItems.length === 0 ? (
                  <option disabled>هیچ قطعه‌ای در این نسخه BOM تعریف نشده است</option>
                ) : (
                  bomItems.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      #{item.sequence_no} - {item.material_revision_display?.name || "قطعه"} ({item.quantity} {item.production_uom_display || item.material_revision_display?.production_uom_code || ""})
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Select Routing Operation */}
            <div style={{ flex: "1 1 280px" }}>
              <label htmlFor="routing-op-select" style={{ fontSize: "0.8rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>
                انتخاب ایستگاه کاری (Routing Operation):
              </label>
              <select
                id="routing-op-select"
                aria-label="انتخاب ایستگاه کاری"
                value={selectedRoutingOpId}
                onChange={(e) => setSelectedRoutingOpId(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--color-border, #E4DCC8)",
                  fontSize: "0.85rem",
                  outline: "none",
                }}
              >
                <option value="" disabled>
                  یک ایستگاه کاری انتخاب کنید...
                </option>
                {routingOpsLoading ? (
                  <option disabled>در حال دریافت ایستگاه‌ها...</option>
                ) : routingOps.length === 0 ? (
                  <option disabled>هیچ ایستگاهی در این نسخه مسیر تعریف نشده است</option>
                ) : (
                  routingOps.map((op: any) => (
                    <option key={op.id} value={op.id}>
                      #{op.sequence_no} - {op.operation_name || op.work_center_name || "ایستگاه کاری"} ({op.operation_code})
                    </option>
                  ))
                )}
              </select>
            </div>

            <button
              type="submit"
              disabled={createMutation.isPending}
              style={{
                padding: "9px 20px",
                borderRadius: "8px",
                background: "#007270",
                color: "#FFFFFF",
                fontWeight: 800,
                fontSize: "0.85rem",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0, 114, 112, 0.3)",
              }}
            >
              {createMutation.isPending ? "در حال ثبت..." : "+ ثبت تخصیص"}
            </button>
          </div>
        </form>
      )}

      {/* Graph and Sidebar (Only when routing revision is selected) */}
      {selectedRoutingRevisionId && (
        <div style={{ display: "flex", gap: "1.5rem", flexDirection: "row", alignItems: "flex-start" }}>
          
          {/* Graph Section */}
          <div className="brand-card-extand" style={{ flex: 2, padding: "1.25rem", overflowX: "auto" }}>
            <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#007270", marginBottom: "1rem" }}>
              🗺️ گراف وابستگی عملیات
            </h3>
            <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "1rem" }}>
              جهت تخصیص یک قطعه، آن را از فرم انتخاب کرده و سپس روی ایستگاه موردنظر در گراف کلیک کنید (یا از طریق فرم انتخاب کنید).
            </p>
            <DependencyGraphEditor 
              routingRevisionId={selectedRoutingRevisionId} 
              operations={routingOps.map((op: any) => ({
                id: op.id,
                operationCode: op.operation_code,
                operationName: op.operation_name,
                sequenceNo: op.sequence_no,
                upstreamJoinType: op.upstream_join_type,
              }))}
              readOnly={true} 
              onNodeClick={(opId) => onNodeClick(opId)} 
            />
          </div>

          {/* Unallocated BOM Items Sidebar */}
          {selectedBomRevisionId && (
            <div className="brand-card-extand" style={{ flex: 1, padding: "1.25rem", maxHeight: "800px", overflowY: "auto" }}>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#D97706", marginBottom: "1rem" }}>
                🧩 قطعات آماده‌ی تخصیص
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {bomItems.map((item: any) => {
                  const isAllocated = allocations.some(a => a.bom_item === item.id && a.is_active);
                  if (isAllocated) return null; // Hide allocated items

                  const isSelected = selectedBomItemId === item.id;
                  return (
                    <div 
                      key={item.id}
                      onClick={() => setSelectedBomItemId(item.id)}
                      style={{ 
                        padding: "10px", 
                        border: `1px solid ${isSelected ? "#007270" : "var(--color-border)"}`,
                        borderRadius: "8px",
                        background: isSelected ? "rgba(0,114,112,0.05)" : "#fff",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <div style={{ fontSize: "0.85rem", fontWeight: "700", color: "#2B2B2B" }}>
                        {item.material_revision_display?.name || "قطعه"}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#666" }}>
                        گام: {item.sequence_no} | مقدار: {item.quantity} {item.production_uom_display || item.material_revision_display?.production_uom_code || ""}
                      </div>
                    </div>
                  );
                })}
                {bomItems.every((item: any) => allocations.some(a => a.bom_item === item.id && a.is_active)) && (
                  <div style={{ textAlign: "center", padding: "1rem", color: "#10B981", fontSize: "0.85rem", fontWeight: "700" }}>
                    🎉 تمامی قطعات این نسخه BOM با موفقیت به ایستگاه‌ها تخصیص داده شده‌اند.
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      )}

      {/* Allocations Table */}
      <div className="brand-card-extand" style={{ padding: "1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#007270" }}>
            📋 لیست تخصیص‌های قطعه به ایستگاه ({filteredAllocations.length})
          </h3>
        </div>

        {allocationsLoading ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>در حال دریافت اطلاعات تخصیص‌ها...</div>
        ) : filteredAllocations.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "2.5rem",
              background: "var(--color-surface, #F5F1E6)",
              borderRadius: "10px",
              border: "1px dashed var(--color-border, #E4DCC8)",
              color: "#666",
              fontSize: "0.85rem",
            }}
          >
            هیچ تخصیصی برای رکوردهای انتخاب‌شده یافت نشد.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--color-border)", color: "#007270" }}>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>ردیف</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>قطعه متصل از BOM</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>ایستگاه کاری متصل (Operation)</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700", borderLeft: "2px dashed var(--color-border)", borderRight: "2px dashed var(--color-border)" }}>منبع مواد</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>وضعیت</th>
                  {canManage && <th style={{ padding: "0.75rem 1rem", fontWeight: "700", textAlign: "center" }}>عملیات</th>}
                </tr>
              </thead>
              <tbody>
                {filteredAllocations.map((alloc, idx) => {
                  const bomDisplay = alloc.bom_item_display;
                  const opDisplay = alloc.routing_operation_display;

                  // Resolve material name from bom_item_display or fetched bomItems
                  const bomItemObj = bomItems.find((i: BomItem) => i.id === alloc.bom_item);
                  const matDisplay = bomItemObj?.material_revision_display;
                  const materialName =
                    bomDisplay?.material_revision_name ||
                    bomDisplay?.material_revision_sku ||
                    matDisplay?.name ||
                    "قطعه فرمول ساخت";
                  const seqNo = bomDisplay?.sequence_no || bomItemObj?.sequence_no;

                  return (
                    <tr key={alloc.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "0.75rem 1rem", fontWeight: "700", color: "#007270", fontSize: "0.85rem" }}>
                        #{idx + 1}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", fontWeight: "700", color: "#2B2B2B" }}>
                        🧩 {materialName}
                        {seqNo && (
                          <span style={{ fontSize: "0.75rem", color: "#D4AF37", marginRight: "6px" }}>
                            [گام BOM #{seqNo}]
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", fontWeight: "700", color: "#2B2B2B" }}>
                        🚉 {opDisplay?.operation_name || `ایستگاه (${alloc.routing_operation.slice(0, 8)})`}
                        {opDisplay?.operation_code && (
                          <span style={{ fontSize: "0.75rem", color: "#888", fontFamily: "monospace", marginRight: "6px" }}>
                            ({opDisplay.operation_code})
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", borderLeft: "2px dashed var(--color-border)", borderRight: "2px dashed var(--color-border)" }}>
                        <select
                          value={materialSources.find((s: any) => s.bom_operation_allocation === alloc.id)?.source_operation || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            const existing = materialSources.find((s: any) => s.bom_operation_allocation === alloc.id);
                            if (val === "") {
                              if (existing) unlinkSourceMutation.mutate({ id: existing.id });
                            } else {
                              if (existing) unlinkSourceMutation.mutate({ id: existing.id }, { onSuccess: () => {
                                linkSourceMutation.mutate({ data: { bom_operation_allocation: alloc.id, consuming_operation: alloc.routing_operation, source_operation: val } });
                              }});
                              else linkSourceMutation.mutate({ data: { bom_operation_allocation: alloc.id, consuming_operation: alloc.routing_operation, source_operation: val } });
                            }
                          }}
                          disabled={linkSourceMutation.isPending || unlinkSourceMutation.isPending}
                          style={{
                            padding: "6px 8px",
                            borderRadius: "6px",
                            border: "1px solid var(--color-border)",
                            fontSize: "0.75rem",
                            outline: "none",
                            width: "100%",
                            maxWidth: "180px",
                          }}
                        >
                          <option value="">بدون منبع (مستقیم)</option>
                          {routingOps.filter((o: any) => o.id !== alloc.routing_operation).map((o: any) => (
                            <option key={o.id} value={o.id}>{o.operation_name}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: "0.75rem 1rem" }}>
                        <span
                          style={{
                            padding: "3px 8px",
                            borderRadius: "6px",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            background: alloc.is_active ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
                            color: alloc.is_active ? "#10B981" : "#EF4444",
                          }}
                        >
                          {alloc.is_active ? "فعال" : "غیرفعال"}
                        </span>
                      </td>
                      {canManage && (
                        <td style={{ padding: "0.75rem 1rem", textAlign: "center" }}>
                          <button
                            onClick={() => handleToggleActive(alloc.id, alloc.is_active)}
                            disabled={activateMutation.isPending || deactivateMutation.isPending}
                            style={{
                              padding: "4px 10px",
                              borderRadius: "6px",
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              background: alloc.is_active ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.1)",
                              color: alloc.is_active ? "#EF4444" : "#10B981",
                              border: `1px solid ${alloc.is_active ? "rgba(239, 68, 68, 0.3)" : "rgba(16, 185, 129, 0.3)"}`,
                              cursor: "pointer",
                            }}
                          >
                            {alloc.is_active ? "⏸️ غیرفعال‌سازی" : "▶️ فعال‌سازی مجدد"}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
